import {
  Snackbar,
  Alert,
  CircularProgress,
  Backdrop,
  Icon,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  MenuItem,
  Button,
} from "@mui/material";

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDInput from "theme/components/MDInput";
import MDButton from "theme/components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import axios from "axios.js";
import usePageTitle from "app/pageTitle";

import DataTable from "theme/Tables/DataTable";
import { useAwaitDialog } from "app/components/status_dialog";

export default function OpsRelationshipPage() {
  const title = "Business Entity";
  usePageTitle(title);

  const { ref } = useParams();
  const navigate = useNavigate();

  const statusDialog = useAwaitDialog();
  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [refreshPage, setRefreshPage] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);

  const [relationship, setRelationship] = useState({});
  const [entityGroups, setEntityGroups] = useState([]);
  const [agentGroups, setAgentGroups] = useState([]);
  const [activities, setActivities] = useState([]);
  const [relationshipActivities, setRelationshipActivities] = useState([]);
  const [entityGroupTitle, setEntityGroupTitle] = useState(null);
  const [openEntityGroupDialog, setOpenEntityGroupDialog] = useState(false);
  const [selectedAgentGroup, setSelectedAgentGroup] = useState(null);

  const [openActivityDialog, setOpenActivityDialog] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);

  function singularize(word) {
    if (!word || typeof word !== "string") return word;

    const irregular = {
      people: "person",
      men: "man",
      women: "woman",
      children: "child",
      teeth: "tooth",
      feet: "foot",
      mice: "mouse",
      geese: "goose",
      data: "datum",
      indices: "index",
      analyses: "analysis",
      crises: "crisis",
      diagnoses: "diagnosis",
      theses: "thesis",
      lives: "life",
      wives: "wife",
      knives: "knife",
      leaves: "leaf",
      shelves: "shelf",
      wolves: "wolf",
    };

    const lower = word.toLowerCase();

    if (irregular[lower]) {
      return matchCase(word, irregular[lower]);
    }

    if (lower.endsWith("ies") && lower.length > 3) {
      return matchCase(word, lower.slice(0, -3) + "y");
    }

    if (/(xes|zes|ches|shes|sses)$/.test(lower)) {
      return matchCase(word, lower.slice(0, -2));
    }

    if (lower.endsWith("ves")) {
      return matchCase(word, lower.slice(0, -3) + "f");
    }

    if (lower.endsWith("s") && !lower.endsWith("ss")) {
      return matchCase(word, lower.slice(0, -1));
    }

    return word;
  }

  function matchCase(original, replacement) {
    return original[0] === original[0].toUpperCase()
      ? replacement.charAt(0).toUpperCase() + replacement.slice(1)
      : replacement;
  }

  async function deleteLocationGroup(groupRef) {
    setShowLoading(true);
    await axios
      .get(`/portal/ops/relationships/entity_groups/${groupRef}/delete.json`)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  const locationGroupsTableColumns = [
    { Header: "Name", accessor: "title", align: "left" },
    { Header: "Code", accessor: "code", align: "left" },
    { Header: "Assigned To", accessor: "agent_group", align: "left" },
    { Header: "Actions", accessor: "actions", align: "right" },
  ];

  const buildLocationGroupsRows = (data) => {
    const rows = [];
    data.map((row) => {
      rows.push({
        title: row.title,
        code: row.code,
        agent_group: row.agent_group,
        actions: (
          <MDBox sx={{ width: "100%" }}>
            <MDBox mt={1} display="flex" justifyContent="space-evenly" alignItems="start">
              <Tooltip title="Delete Group?">
                <MDButton
                  size="small"
                  variant="outlined"
                  color="error"
                  iconOnly
                  onClick={() => {
                    if (window.confirm(`Delete group?`) === true) {
                      deleteLocationGroup(row.reference);
                    }
                  }}
                >
                  <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                    delete
                  </Icon>
                </MDButton>
              </Tooltip>
            </MDBox>
          </MDBox>
        ),
        created: row.created,
      });
      return null;
    });

    return rows;
  };

  async function deleteActivityRelationship(relRef) {
    setShowLoading(true);
    await axios
      .get(`/portal/ops/relationships/activities/${relRef}/delete.json`)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  const activityTableColumns = [
    { Header: "Activity Name", accessor: "title", align: "left" },
    { Header: "Actions", accessor: "actions", align: "right" },
  ];

  const buildActivityRows = (data) => {
    const rows = [];
    data.map((row) => {
      rows.push({
        title: row.title,
        actions: (
          <MDBox sx={{ width: "100%" }}>
            <MDBox mt={1} display="flex" gap={0.5}>
              <Tooltip title="Activity Logs">
                <MDButton
                  size="small"
                  variant="outlined"
                  color="dark"
                  iconOnly
                  onClick={() => {
                    navigate(`/ops/activities/${row.activity}/relationship/${row.reference}/logs`)
                  }}
                >
                  <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                    list
                  </Icon>
                </MDButton>
              </Tooltip>
              <Tooltip title="Team Performance">
                <MDButton
                  size="small"
                  variant="outlined"
                  color="warning"
                  iconOnly
                  onClick={() => {
                    navigate(`/ops/activities/performance/${row.reference}`)
                  }}
                >
                  <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                    bar_chart
                  </Icon>
                </MDButton>
              </Tooltip>
              <Tooltip title="Remove Activity?">
                <MDButton
                  size="small"
                  variant="outlined"
                  color="error"
                  iconOnly
                  onClick={() => {
                    if (window.confirm(`Remove activity link?`) === true) {
                      deleteActivityRelationship(row.reference);
                    }
                  }}
                >
                  <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                    delete
                  </Icon>
                </MDButton>
              </Tooltip>
            </MDBox>
          </MDBox>
        ),
      });
      return null;
    });

    return rows;
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`portal/ops/relationships/${ref}/fetch.json`)
        .then((result) => {
          setShowLoading(false);
          console.log(result.data.data);
          if (result.data.status === "success") {
            setRelationship(result.data.data.relationship);
            setAgentGroups(result.data.data.agent_groups);

            setActivities(result.data.data.activities);

            const actRows = buildActivityRows(result.data.data.activity_relationships);
            setRelationshipActivities(actRows);

            const groupRows = buildLocationGroupsRows(result.data.data.entity_groups);
            setEntityGroups(groupRows);

            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, [refreshPage]);

  async function createEntityGroup() {
    if (entityGroupTitle === null) {
      setSeverity("error");
      setErrMsg("Group name is required.");
      setOpen(true);
      return;
    }

    setOpenEntityGroupDialog(false);
    setShowLoading(true);
    const json = {
      reference: ref,
      title: entityGroupTitle,
      group: selectedAgentGroup,
    };

    await axios
      .post("/portal/ops/relationships/entity_groups/create.json", json)
      .then(async (result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          await statusDialog.show(result.data.data.message, "success", "Success!");

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  async function addActivityRelationship() {
    if (selectedActivity === null) {
      setSeverity("error");
      setErrMsg("Activity is required.");
      setOpen(true);
      return;
    }

    setOpenActivityDialog(false);
    setShowLoading(true);
    const json = {
      reference: ref,
      activity: selectedActivity,
    };

    await axios
      .post("/portal/ops/relationships/activities/create.json", json)
      .then(async (result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          await statusDialog.show(result.data.data.message, "success", "Success!");

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      {/* Entity Group Dialog */}
      <Dialog open={openEntityGroupDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDBox>Create {singularize(relationship.name)} Group</MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox>
            <MDBox mt={0}>
              <MDInput
                name="title"
                margin="dense"
                label="Name"
                type="text"
                fullWidth
                variant="outlined"
                onChange={(e) => setEntityGroupTitle(e.target.value)}
              />
            </MDBox>
            <MDBox mt={3}>
              <MDInput
                select
                label="Assigned Agent Group"
                onChange={(e) => {
                  setSelectedAgentGroup(e.target.value);
                }}
                fullWidth
                SelectProps={{ style: { padding: "12px 0px" } }}
              >
                {agentGroups.length > 0 &&
                  agentGroups.map((record) => (
                    <MenuItem key={record.reference} value={`${record.reference}`}>
                      {record.title}
                    </MenuItem>
                  ))}
              </MDInput>
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenEntityGroupDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="dark"
            variant="contained"
            onClick={() => {
              createEntityGroup();
            }}
          >
            Create
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Activity Relationship Dialog */}
      <Dialog open={openActivityDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDBox>Link Activity</MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox mt={1}>
            <MDInput
              select
              label="Select Activity"
              onChange={(e) => {
                setSelectedActivity(e.target.value);
              }}
              fullWidth
              SelectProps={{ style: { padding: "12px 0px" } }}
            >
              {activities.length > 0 &&
                activities.map((record) => (
                  <MenuItem key={record.reference} value={`${record.reference}`}>
                    {record.title}
                  </MenuItem>
                ))}
            </MDInput>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenActivityDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="dark"
            variant="contained"
            onClick={() => {
              addActivityRelationship();
            }}
          >
            Add
          </MDButton>
        </DialogActions>
      </Dialog>

      {pageLoaded && (
        <MDBox mb={5}>
          <MDBox mt={0} mb={1} display="flex" justifyContent="space-between">
            <MDBox>
              <Button
                color="dark"
                size="small"
                startIcon={<Icon>west</Icon>}
                onClick={() => {
                  navigate(`/ops`);
                }}
              >
                Back
              </Button>
            </MDBox>
            <MDBox>
              <Button
                color="dark"
                size="small"
                startIcon={<Icon>handshake</Icon>}
                onClick={() => {
                  navigate(`/ops/entities/${ref}`);
                }}
              >
                {relationship.name}
              </Button>
            </MDBox>
          </MDBox>

          {/* Entity Groups Section */}
          <MDBox mt={2}>
            {entityGroups.length > 0 ? (
              <MDBox>
                <Card sx={{ height: "100%", marginTop: "0px" }}>
                  <MDBox>
                    <MDBox p={2}>
                      <MDBox display="flex" justifyContent="space-between" alignItems="center">
                        <MDBox>
                          <MDTypography variant="h6" fontWeight="medium">
                            {singularize(relationship.name)} Groups
                          </MDTypography>
                        </MDBox>
                        <MDBox>
                          <MDButton
                            variant="contained"
                            color="dark"
                            iconOnly
                            size="small"
                            onClick={() => {
                              setEntityGroupTitle(null);
                              setOpenEntityGroupDialog(true);
                            }}
                          >
                            <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                          </MDButton>
                        </MDBox>
                      </MDBox>
                    </MDBox>
                    <MDBox>
                      <DataTable
                        table={{ columns: locationGroupsTableColumns, rows: entityGroups }}
                        isSorted
                        entriesPerPage={false}
                        showTotalEntries
                        buttons
                        noEndBorder
                      />
                    </MDBox>
                  </MDBox>
                </Card>
              </MDBox>
            ) : (
              <MDBox>
                <Card sx={{ height: "100%", marginTop: "0px" }}>
                  <MDBox p={2}>
                    <MDBox align="center">
                      <MDBox>
                        <MDTypography variant="h6" fontSize={48}>
                          <Icon>workspaces</Icon>
                        </MDTypography>
                        <MDTypography variant="h6" fontWeight="light" fontSize={16}>
                          Organise {relationship.name.toLowerCase()} into groups.
                          <br />
                          Add {relationship.name.toLowerCase()} to each group and assign groups to
                          your team.
                        </MDTypography>
                      </MDBox>
                      <MDBox mt={3} mb={3}>
                        <MDButton
                          variant="contained"
                          color="dark"
                          onClick={() => {
                            setEntityGroupTitle(null);
                            setOpenEntityGroupDialog(true);
                          }}
                          startIcon={<Icon>add</Icon>}
                        >
                          Add {singularize(relationship.name)} Group
                        </MDButton>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </Card>
              </MDBox>
            )}
          </MDBox>

          {/* Activity Relationships Section */}
          <MDBox mt={3}>
            {relationshipActivities.length > 0 ? (
              <MDBox>
                <Card sx={{ height: "100%", marginTop: "0px" }}>
                  <MDBox>
                    <MDBox p={2}>
                      <MDBox display="flex" justifyContent="space-between" alignItems="center">
                        <MDBox>
                          <MDTypography variant="h6" fontWeight="medium">
                            Activities
                          </MDTypography>
                        </MDBox>
                        <MDBox>
                          <MDButton
                            variant="contained"
                            color="dark"
                            iconOnly
                            size="small"
                            onClick={() => {
                              setSelectedActivity(null);
                              setOpenActivityDialog(true);
                            }}
                          >
                            <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                          </MDButton>
                        </MDBox>
                      </MDBox>
                    </MDBox>
                    <MDBox>
                      <DataTable
                        table={{ columns: activityTableColumns, rows: relationshipActivities }}
                        isSorted
                        entriesPerPage={false}
                        showTotalEntries
                        buttons
                        noEndBorder
                      />
                    </MDBox>
                  </MDBox>
                </Card>
              </MDBox>
            ) : (
              <MDBox>
                <Card sx={{ height: "100%", marginTop: "0px" }}>
                  <MDBox p={2}>
                    <MDBox align="center">
                      <MDBox>
                        <MDTypography variant="h6" fontSize={48}>
                          <Icon>task</Icon>
                        </MDTypography>
                        <MDTypography variant="h6" fontWeight="light" fontSize={16}>
                          Link activities to {relationship.name.toLowerCase()}.
                          <br />
                          Specify activities that apply to your {relationship.name.toLowerCase()}.
                        </MDTypography>
                      </MDBox>
                      <MDBox mt={3} mb={3}>
                        <MDButton
                          variant="contained"
                          color="dark"
                          onClick={() => {
                            setSelectedActivity(null);
                            setOpenActivityDialog(true);
                          }}
                          startIcon={<Icon>add</Icon>}
                        >
                          Link Activity
                        </MDButton>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </Card>
              </MDBox>
            )}
          </MDBox>
        </MDBox>
      )}

      <Footer />
    </DashboardLayout>
  );
}
